import type { ToastService } from '../services/toast.service';

/**
 * A failed dynamic `import()` — almost always a stale-deploy artefact: the browser's cached
 * `index.html` points at hashed chunk files the new build deleted, so the request 404s and (when
 * the host serves the SPA fallback) comes back as `text/html`, which the module loader rejects with
 * "'text/html' is not a valid JavaScript MIME type". Chromium/Firefox/WebKit each phrase this
 * differently, hence the alternation.
 */
const CHUNK_LOAD_RE =
  /Loading chunk|dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

export function isChunkLoadError(err: unknown): boolean {
  let msg = '';
  if (err instanceof Error) msg = err.message;
  else if (typeof err === 'string') msg = err;
  else if (err && typeof err === 'object' && 'message' in err) msg = String(err.message);
  return CHUNK_LOAD_RE.test(msg);
}

/**
 * Persistent, actionable toast for the stale-deploy case — the one "unknown" error the user can
 * actually fix. De-duped against the currently-shown toasts (so the navigation-error handler and
 * the global `ErrorHandler` both firing can't stack it), but re-shows if the user dismissed a
 * previous one without reloading. `translate` is passed in rather than injected because this runs
 * from the global `ErrorHandler`, outside a component's injection scope.
 */
export function promptReloadForNewVersion(
  toast: ToastService,
  translate: (key: string) => string,
): void {
  const title = translate('common.newVersion.title');
  if (toast.toasts().some((t) => t.title === title)) return;
  toast.error({
    title,
    description: translate('common.newVersion.description'),
    durationMs: 0,
    action: {
      label: translate('common.newVersion.reload'),
      run: () => location.reload(),
    },
  });
}
